import { memo, useEffect, useState } from 'react';
import { bundledLanguages, codeToHtml, isSpecialLang, type BundledLanguage, type SpecialLanguage } from 'shiki';
import DOMPurify from 'dompurify';
import { classNames } from '~/utils/classNames';
import { createScopedLogger } from '~/utils/logger';

import styles from './CodeBlock.module.scss';

// Security: configure DOMPurify to only allow safe HTML elements from Shiki output
const ALLOWED_TAGS = ['span', 'div', 'br', 'code', 'pre'];
const ALLOWED_ATTR = ['class', 'style'];

const logger = createScopedLogger('CodeBlock');

/** Above this many chars we skip Shiki entirely and render plain text. Tokenizing
 *  a huge code/tool-result dump synchronously is a primary main-thread-freeze
 *  cause on reopen; plain text stays readable, selectable, and copyable. */
export const MAX_HIGHLIGHT_CHARS = 30_000;

/** ms of "no change" before we highlight. While a code block streams in it grows
 *  every token; without this we'd re-tokenize on every token (the streaming
 *  freeze). We show plain text until it settles, then highlight once. */
const HIGHLIGHT_DEBOUNCE_MS = 150;

/** True when a block is too large to highlight synchronously — render plain. */
export function exceedsHighlightLimit(code: string): boolean {
  return code.length > MAX_HIGHLIGHT_CHARS;
}

interface CodeBlockProps {
  className?: string;
  code: string;
  language?: BundledLanguage | SpecialLanguage;
  theme?: 'light-plus' | 'dark-plus';
  disableCopy?: boolean;
}

export const CodeBlock = memo(
  ({ className, code, language = 'plaintext', theme = 'dark-plus', disableCopy = false }: CodeBlockProps) => {
    const [html, setHTML] = useState<string | undefined>(undefined);
    const [copied, setCopied] = useState(false);

    const copyToClipboard = () => {
      if (copied) {
        return;
      }

      navigator.clipboard.writeText(code);

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    };

    useEffect(() => {
      // Very large blocks: never run Shiki — render the plain <pre> fallback.
      if (exceedsHighlightLimit(code)) {
        setHTML(undefined);
        return undefined;
      }

      // Reset to the plain fallback immediately so a growing (streaming) block
      // shows its LIVE text, never a stale highlight. It re-highlights only once
      // the code settles (debounced) — so we don't re-tokenize per token.
      setHTML(undefined);

      let effectiveLanguage = language;

      if (language && !isSpecialLang(language) && !(language in bundledLanguages)) {
        logger.warn(`Unsupported language '${language}', falling back to plaintext`);
        effectiveLanguage = 'plaintext';
      }

      let cancelled = false;

      const timer = setTimeout(async () => {
        try {
          const rawHtml = await codeToHtml(code, { lang: effectiveLanguage, theme });

          // Superseded by a newer code value (another token arrived) — drop this
          // stale result instead of overwriting the live block.
          if (cancelled) {
            return;
          }

          // Security: sanitize Shiki output to prevent XSS from malicious snippets.
          setHTML(DOMPurify.sanitize(rawHtml, { ALLOWED_TAGS, ALLOWED_ATTR }));
        } catch (err) {
          if (!cancelled) {
            logger.warn(`Highlight failed, keeping plain text: ${err instanceof Error ? err.message : String(err)}`);
          }
        }
      }, HIGHLIGHT_DEBOUNCE_MS);

      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }, [code, language, theme]);

    return (
      <div className={classNames('relative group text-left', className)}>
        <div
          className={classNames(
            styles.CopyButtonContainer,
            'bg-transparant absolute top-[10px] right-[10px] rounded-md z-10 text-lg flex items-center justify-center opacity-0 group-hover:opacity-100 touch:opacity-100',
            {
              'rounded-l-0 opacity-100': copied,
            },
          )}
        >
          {!disableCopy && (
            <button
              className={classNames(
                'flex items-center bg-accent text-[#0A0A0A] p-[6px] justify-center before:bg-white before:rounded-l-md before:text-gray-500 before:border-r before:border-gray-300 rounded-md transition-theme',
                {
                  'before:opacity-0': !copied,
                  'before:opacity-100': copied,
                },
              )}
              title="Copy Code"
              onClick={() => copyToClipboard()}
            >
              <div className="i-ph:clipboard-text-duotone"></div>
            </button>
          )}
        </div>
        {html !== undefined ? (
          <div dangerouslySetInnerHTML={{ __html: html }}></div>
        ) : (
          // Plain fallback: shown while a streaming block hasn't settled yet, or
          // for blocks too large to highlight. React escapes {code}, so no XSS.
          <pre className="overflow-x-auto p-4 text-[13px] leading-relaxed font-mono whitespace-pre">
            <code>{code}</code>
          </pre>
        )}
      </div>
    );
  },
);
